import { useMemo } from 'react';

import { useQuery } from '@apollo/client/react';
import { css, Global } from '@emotion/react';
import { Print } from '@mui/icons-material';
import { Stack, Paper, Typography, IconButton } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Box } from '@mui/system';
import {
  DatePickersProvider,
  Image,
  MatchsPlannerEmpty,
  MatchsPlannerProvider,
  MatchsPlannerWeekController,
  useMatchsPlannerContext,
} from '@scorenco/components';
import type { GetStaticPaths, GetStaticProps } from 'next';
import { useRouter } from 'next/router';
import { serverSideTranslations } from 'next-i18next/pages/serverSideTranslations';
import { addDays } from 'date-fns';

import { FooterScorenco } from '@/components';
import { pickTextColorBasedOnBgColorSimple } from '@/utils';
import { Types } from '@scorenco/core';
import type { SerializedStyles } from '@emotion/react';

import { MatchPlanningForm } from './_internal/MatchPlanningForm';
import {
  GetClubInfosDocument,
  GetWidgetDocument,
} from './_internal/Widget.query.generated';

interface WidgetConvocationContentProps {
  club: Types.Clubs_Club;
  style: { root: SerializedStyles; globals: SerializedStyles };
}

const getNumberArrayFromUnknown = (value: unknown): number[] | undefined => {
  if (!Array.isArray(value)) {
    return undefined;
  }

  return value.filter((item): item is number => typeof item === 'number');
};

const WidgetConvocationContent = ({
  club,
  style,
}: WidgetConvocationContentProps) => {
  const { startDate } = useMatchsPlannerContext();
  const router = useRouter();

  const getWidget = useQuery(GetWidgetDocument, {
    variables: {
      widgetId: Number(router.query.widgetId),
      cacheTTL: router.query.noCache ? 0 : undefined,
    },
    skip: !router.query.widgetId,
    context: {
      role: 'anonymous',
    },
  });

  const convocationSettings =
    getWidget?.data?.widgets_widget_by_pk?.convocation_settings;
  const safeConvocationSettings = convocationSettings
    ? {
        __typename: 'widgets_convocation_settings' as const,
        exclude_task_ids: getNumberArrayFromUnknown(
          convocationSettings.exclude_task_ids
        ),
      }
    : {
        __typename: 'widgets_convocation_settings' as const,
      };

  return (
    <Paper css={style.root} variant="bordered">
      <Box className="header">
        <Image src={club.logo} className="club-logo" width={80} height={80} />
        <Typography variant="h4">Convocations - {club.name}</Typography>
      </Box>

      <Stack spacing={3}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <MatchsPlannerWeekController sx={{ mt: 2, mx: 2 }} />
          <IconButton
            color="primary"
            href={`${process.env.NEXT_PUBLIC_BACKOFFICE_URL}/${
              club.id
            }/matchs/planner/export?date=${
              addDays(startDate, 1).toISOString().split('T')[0]
            }`}
            target="_BLANK"
          >
            <Print />
          </IconButton>
        </Box>
        <MatchsPlannerEmpty withCta sx={{ m: 2 }} />
        <MatchPlanningForm
          club={club}
          convocationSettings={safeConvocationSettings}
        />
      </Stack>

      <FooterScorenco
        css={css`
          margin-top: 0;
        `}
      />
    </Paper>
  );
};

const WidgetConvocation = () => {
  const router = useRouter();
  const muiTheme = useTheme();

  const getWidget = useQuery(GetWidgetDocument, {
    variables: {
      widgetId: Number(router.query.widgetId),
      cacheTTL: router.query.noCache ? 0 : undefined,
    },
    skip: !router.query.widgetId,
    context: {
      role: 'anonymous',
    },
  });

  const settings = getWidget?.data?.widgets_widget_by_pk?.settings;

  const widgetStyle = useMemo(() => {
    if (
      !settings?.background_color ||
      !settings?.color ||
      !settings?.font_color
    ) {
      return undefined;
    }

    return css`
      --snc-palette-primary-main: ${settings.color};
      --snc-palette-primary-contrastText: ${pickTextColorBasedOnBgColorSimple(
        settings.color,
        '#ffffff',
        '#000000'
      )};
      --snc-palette-text-primary: ${settings.font_color};
      --snc-palette-background-default: ${settings.background_color};
      --snc-palette-secondary-main: ${settings.color};
    `;
  }, [settings?.background_color]);

  const style = {
    root: css`
      display: flex;
      padding: 0;
      overflow: hidden;
      align-items: stretch;
      color: ${muiTheme.vars.palette.text.primary};

      .header {
        width: 100%;
        display: flex;
        flex-direction: row;
        align-items: center;
        padding: 0.5rem 1.5rem;
        background: ${muiTheme.vars.palette.primary.main};
        color: ${muiTheme.vars.palette.primary.contrastText};

        .club-logo {
          height: 2.5rem;
          object-fit: contain;
          margin-right: 1rem;
        }
      }
      ${widgetStyle}
    `,
    globals: css`
      body {
        background: transparent;
      }
    `,
  };

  const getClubInfos = useQuery(GetClubInfosDocument, {
    variables: {
      clubId: Number(getWidget?.data?.widgets_widget_by_pk?.club_id),
      cacheTTL: router.query.noCache ? 0 : undefined,
    },
    skip: !getWidget?.data?.widgets_widget_by_pk,
    context: {
      role: 'anonymous',
    },
  });

  const club = getClubInfos?.data?.clubs_club_by_pk;

  if (!club) {
    return null;
  }

  return (
    <>
      <Global styles={style.globals} />
      <DatePickersProvider>
        <MatchsPlannerProvider
          clubId={club.id}
          usePlannerInfo={true}
          disablePlannerSettings={true}
        >
          <WidgetConvocationContent club={club} style={style} />
        </MatchsPlannerProvider>
      </DatePickersProvider>
    </>
  );
};

export const getStaticPaths: GetStaticPaths = () => ({
  paths: [],
  fallback: 'blocking',
});

export const getStaticProps: GetStaticProps = async ({ locale }) => ({
  props: {
    ...(await serverSideTranslations(locale ?? 'fr', [
      'snc-components',
      'snc-core',
      'common',
    ])),
  },
});

export default WidgetConvocation;
